import React, { useState } from 'react';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { DashboardSidebar } from '../components/dashboard/DashboardSidebar';
import { HelpCircleIcon, BookIcon, MessageCircleIcon, PhoneIcon, MailIcon, ChevronDownIcon, ChevronRightIcon, ExternalLinkIcon, PlayCircleIcon, FileTextIcon, SearchIcon, CheckIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
export function HelpDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  // Mock user data
  const [userData] = useState({
    name: 'FATHALLAH Mehdi',
    company: 'Tunipages',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    email: 'mehdi.fathallah@tunipages.com'
  });
  // Mock FAQ data
  const faqs = [{
    id: 1,
    question: "Comment créer une alerte pour les appels d'offres ?",
    answer: "Pour créer une alerte, rendez-vous dans la section 'Mes alertes' et cliquez sur le bouton 'Nouvelle alerte'. Vous pourrez ensuite définir vos critères de recherche (secteur d'activité, région, mots-clés, etc.) et choisir la fréquence de notification. Une fois votre alerte créée, vous recevrez des notifications lorsque de nouveaux appels d'offres correspondant à vos critères seront publiés."
  }, {
    id: 2,
    question: "Comment télécharger un document d'appel d'offres ?",
    answer: "Pour télécharger un document d'appel d'offres, ouvrez la page détaillée de l'appel d'offres qui vous intéresse, puis cliquez sur le bouton 'Télécharger' à côté du document souhaité. Notez que le nombre de téléchargements peut être limité selon votre forfait d'abonnement."
  }, {
    id: 3,
    question: 'Comment puis-je mettre à jour mes informations de facturation ?',
    answer: "Pour mettre à jour vos informations de facturation, accédez à la section 'Paramètres', puis cliquez sur l'onglet 'Facturation'. Vous pourrez y modifier vos coordonnées, mettre à jour votre mode de paiement et consulter l'historique de vos factures."
  }, {
    id: 4,
    question: "Est-il possible de partager un appel d'offres avec un collègue ?",
    answer: "Oui, vous pouvez partager un appel d'offres avec un collègue. Sur la page détaillée de l'appel d'offres, cliquez sur le bouton 'Partager' et saisissez l'adresse email de votre collègue. Il recevra un lien pour accéder directement à l'appel d'offres (il devra avoir un compte sur la plateforme pour y accéder)."
  }, {
    id: 5,
    question: "Comment puis-je changer de formule d'abonnement ?",
    answer: "Pour changer de formule d'abonnement, rendez-vous dans la section 'Paramètres', puis cliquez sur l'onglet 'Abonnement'. Vous y trouverez les différentes formules disponibles et pourrez sélectionner celle qui correspond le mieux à vos besoins. Le changement prendra effet à la fin de votre période de facturation en cours."
  }, {
    id: 6,
    question: 'Comment contacter le support technique ?',
    answer: "Vous pouvez contacter notre support technique par email à support@elevateit.fr, par téléphone au +33 7 53 29 34 19 (du lundi au vendredi de 9h à 18h), ou en utilisant le formulaire de contact disponible dans la section 'Aide' de votre espace client."
  }];
  // Mock tutorials data
  const tutorials = [{
    id: 1,
    title: 'Comment configurer vos alertes',
    description: "Apprenez à configurer des alertes personnalisées pour ne manquer aucun appel d'offres pertinent",
    duration: '3:45',
    thumbnail: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
    type: 'video'
  }, {
    id: 2,
    title: 'Optimiser vos recherches',
    description: "Découvrez les astuces pour affiner vos recherches et trouver rapidement les appels d'offres qui vous intéressent",
    duration: '5:12',
    thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
    type: 'video'
  }, {
    id: 3,
    title: "Guide d'utilisation complet",
    description: "Téléchargez notre guide d'utilisation détaillé pour maîtriser toutes les fonctionnalités de la plateforme",
    pages: '24 pages',
    thumbnail: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
    type: 'pdf'
  }, {
    id: 4,
    title: 'Exporter et analyser les données',
    description: "Apprenez à exporter les données et à utiliser les outils d'analyse pour prendre de meilleures décisions",
    duration: '4:30',
    thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60',
    type: 'video'
  }];
  // Toggle FAQ item
  const toggleFAQ = (id: number) => {
    if (openFAQ === id) {
      setOpenFAQ(null);
    } else {
      setOpenFAQ(id);
    }
  };
  // Filter FAQs based on search query
  const filteredFAQs = faqs.filter(faq => faq.question.toLowerCase().includes(searchQuery.toLowerCase()) || faq.answer.toLowerCase().includes(searchQuery.toLowerCase()));
  return <div className="min-h-screen bg-gray-50" data-id="element-2572">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && <div className="fixed inset-0 bg-gray-800 bg-opacity-50 z-20 md:hidden" onClick={() => setSidebarOpen(false)} data-id="element-2573" />}
      {/* Sidebar */}
      <DashboardSidebar open={sidebarOpen} setOpen={setSidebarOpen} data-id="element-2574" />
      {/* Main content */}
      <div className="md:pl-64" data-id="element-2575">
        {/* Header */}
        <DashboardHeader user={userData} onMenuClick={() => setSidebarOpen(true)} data-id="element-2576" />
        {/* Main content */}
        <main className="py-6 px-4 sm:px-6 lg:px-8" data-id="element-2577">
          {/* Page header */}
          <div className="mb-6" data-id="element-2578">
            <div className="flex items-center mb-4" data-id="element-2579">
              <HelpCircleIcon size={24} className="text-blue-600 mr-3" data-id="element-2580" />
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-2581">
                Centre d'aide
              </h1>
            </div>
            <p className="text-gray-600" data-id="element-2582">
              Trouvez des réponses à vos questions et découvrez comment tirer le
              meilleur parti de notre plateforme
            </p>
          </div>
          {/* Search bar */}
          <div className="mb-8" data-id="element-2583">
            <div className="relative" data-id="element-2584">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2585">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-2586" />
              </div>
              <input type="text" className="block w-full bg-white border border-gray-300 rounded-md py-3 pl-10 pr-3 text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Rechercher dans le centre d'aide..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2587" />
            </div>
          </div>
          {/* Help categories */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-2588">
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow" data-id="element-2589">
              <div className="flex items-center mb-4" data-id="element-2590">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mr-4" data-id="element-2591">
                  <BookIcon size={24} data-id="element-2592" />
                </div>
                <h2 className="text-lg font-medium text-gray-900" data-id="element-2593">
                  Documentation
                </h2>
              </div>
              <p className="text-gray-500 mb-4" data-id="element-2594">
                Consultez notre documentation détaillée pour comprendre toutes
                les fonctionnalités
              </p>
              <a href="#" className="text-blue-600 hover:text-blue-800 font-medium flex items-center" data-id="element-2595">
                Voir la documentation
                <ChevronRightIcon size={16} className="ml-1" data-id="element-2596" />
              </a>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow" data-id="element-2597">
              <div className="flex items-center mb-4" data-id="element-2598">
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center text-yellow-600 mr-4" data-id="element-2599">
                  <PlayCircleIcon size={24} data-id="element-2600" />
                </div>
                <h2 className="text-lg font-medium text-gray-900" data-id="element-2601">
                  Tutoriels vidéo
                </h2>
              </div>
              <p className="text-gray-500 mb-4" data-id="element-2602">
                Regardez nos tutoriels vidéo pour apprendre à utiliser la
                plateforme étape par étape
              </p>
              <a href="#tutorials" className="text-blue-600 hover:text-blue-800 font-medium flex items-center" data-id="element-2603">
                Voir les tutoriels
                <ChevronRightIcon size={16} className="ml-1" data-id="element-2604" />
              </a>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow" data-id="element-2605">
              <div className="flex items-center mb-4" data-id="element-2606">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-600 mr-4" data-id="element-2607">
                  <MessageCircleIcon size={24} data-id="element-2608" />
                </div>
                <h2 className="text-lg font-medium text-gray-900" data-id="element-2609">
                  Support client
                </h2>
              </div>
              <p className="text-gray-500 mb-4" data-id="element-2610">
                Contactez notre équipe de support pour toute question ou
                assistance personnalisée
              </p>
              <a href="#contact" className="text-blue-600 hover:text-blue-800 font-medium flex items-center" data-id="element-2611">
                Contacter le support
                <ChevronRightIcon size={16} className="ml-1" data-id="element-2612" />
              </a>
            </div>
          </div>
          {/* FAQ section */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 mb-8" data-id="element-2613">
            <div className="px-6 py-5 border-b border-gray-200" data-id="element-2614">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-2615">
                Questions fréquemment posées
              </h2>
            </div>
            <div className="p-6" data-id="element-2616">
              {searchQuery && filteredFAQs.length === 0 ? <div className="text-center py-8" data-id="element-2617">
                  <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-4" data-id="element-2618">
                    <SearchIcon size={24} data-id="element-2619" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-2620">
                    Aucun résultat trouvé
                  </h3>
                  <p className="text-gray-500" data-id="element-2621">
                    Essayez avec d'autres termes ou contactez notre support
                  </p>
                </div> : <div className="space-y-4" data-id="element-2622">
                  {filteredFAQs.map(faq => <div key={faq.id} className="border border-gray-200 rounded-md overflow-hidden" data-id="element-2623">
                      <button className="w-full flex justify-between items-center p-4 text-left font-medium text-gray-900 hover:bg-gray-50 focus:outline-none" onClick={() => toggleFAQ(faq.id)} data-id="element-2624">
                        <span data-id="element-2625">{faq.question}</span>
                        {openFAQ === faq.id ? <ChevronDownIcon className="h-5 w-5 text-gray-500" data-id="element-2626" /> : <ChevronRightIcon className="h-5 w-5 text-gray-500" data-id="element-2627" />}
                      </button>
                      {openFAQ === faq.id && <div className="px-4 pb-4 text-gray-600 border-t border-gray-100" data-id="element-2628">
                          <p data-id="element-2629">{faq.answer}</p>
                        </div>}
                    </div>)}
                </div>}
            </div>
          </div>
          {/* Tutorials section */}
          <div id="tutorials" className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 mb-8" data-id="element-2630">
            <div className="px-6 py-5 border-b border-gray-200" data-id="element-2631">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-2632">
                Tutoriels et guides
              </h2>
            </div>
            <div className="p-6" data-id="element-2633">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2634">
                {tutorials.map(tutorial => <div key={tutorial.id} className="flex border border-gray-200 rounded-md overflow-hidden hover:shadow-md transition-shadow" data-id="element-2635">
                    <div className="w-1/3 flex-shrink-0" data-id="element-2636">
                      <img src={tutorial.thumbnail} alt={tutorial.title} className="w-full h-full object-cover" data-id="element-2637" />
                    </div>
                    <div className="w-2/3 p-4" data-id="element-2638">
                      <h3 className="font-medium text-gray-900 mb-1" data-id="element-2639">
                        {tutorial.title}
                      </h3>
                      <p className="text-sm text-gray-500 mb-2" data-id="element-2640">
                        {tutorial.description}
                      </p>
                      <div className="flex items-center text-xs text-gray-500" data-id="element-2641">
                        {tutorial.type === 'video' ? <>
                            <PlayCircleIcon size={14} className="mr-1" data-id="element-2642" />
                            <span data-id="element-2643">{tutorial.duration}</span>
                          </> : <>
                            <FileTextIcon size={14} className="mr-1" data-id="element-2644" />
                            <span data-id="element-2645">{tutorial.pages}</span>
                          </>}
                      </div>
                      <a href="#" className="mt-2 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800" data-id="element-2646">
                        {tutorial.type === 'video' ? 'Regarder' : 'Télécharger'}
                        <ExternalLinkIcon size={14} className="ml-1" data-id="element-2647" />
                      </a>
                    </div>
                  </div>)}
              </div>
              <div className="mt-4 text-center" data-id="element-2648">
                <Button variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50" data-id="element-2649">
                  Voir tous les tutoriels
                </Button>
              </div>
            </div>
          </div>
          {/* Contact section */}
          <div id="contact" className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100" data-id="element-2650">
            <div className="px-6 py-5 border-b border-gray-200" data-id="element-2651">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-2652">
                Contactez-nous
              </h2>
            </div>
            <div className="p-6" data-id="element-2653">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2654">
                <div data-id="element-2655">
                  <h3 className="text-base font-medium text-gray-900 mb-4" data-id="element-2656">
                    Nous sommes là pour vous aider
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-2657">
                    Si vous ne trouvez pas la réponse à votre question,
                    n'hésitez pas à nous contacter directement. Notre équipe de
                    support se fera un plaisir de vous aider.
                  </p>
                  <div className="space-y-4" data-id="element-2658">
                    <div className="flex items-start" data-id="element-2659">
                      <div className="flex-shrink-0 mt-0.5" data-id="element-2660">
                        <PhoneIcon size={18} className="text-blue-600" data-id="element-2661" />
                      </div>
                      <div className="ml-3" data-id="element-2662">
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-2663">
                          Par téléphone
                        </h4>
                        <p className="mt-1 text-sm text-gray-600" data-id="element-2664">
                          +33 7 53 29 34 19
                        </p>
                        <p className="mt-1 text-xs text-gray-500" data-id="element-2665">
                          Lundi au vendredi, 9h-18h
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-2666">
                      <div className="flex-shrink-0 mt-0.5" data-id="element-2667">
                        <MailIcon size={18} className="text-blue-600" data-id="element-2668" />
                      </div>
                      <div className="ml-3" data-id="element-2669">
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-2670">
                          Par email
                        </h4>
                        <p className="mt-1 text-sm text-gray-600" data-id="element-2671">
                          support@elevateit.fr
                        </p>
                        <p className="mt-1 text-xs text-gray-500" data-id="element-2672">
                          Nous répondons sous 24h
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-2673">
                      <div className="flex-shrink-0 mt-0.5" data-id="element-2674">
                        <MessageCircleIcon size={18} className="text-blue-600" data-id="element-2675" />
                      </div>
                      <div className="ml-3" data-id="element-2676">
                        <h4 className="text-sm font-medium text-gray-900" data-id="element-2677">
                          Chat en direct
                        </h4>
                        <p className="mt-1 text-sm text-gray-600" data-id="element-2678">
                          Disponible depuis votre espace client
                        </p>
                        <p className="mt-1 text-xs text-gray-500" data-id="element-2679">
                          Lundi au vendredi, 9h-17h
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div data-id="element-2680">
                  <h3 className="text-base font-medium text-gray-900 mb-4" data-id="element-2681">
                    Envoyez-nous un message
                  </h3>
                  <form className="space-y-4" data-id="element-2682">
                    <div data-id="element-2683">
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2684">
                        Sujet
                      </label>
                      <select id="subject" name="subject" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500" defaultValue="" data-id="element-2685">
                        <option value="" disabled data-id="element-2686">
                          Sélectionnez un sujet
                        </option>
                        <option value="technical" data-id="element-2687">Problème technique</option>
                        <option value="billing" data-id="element-2688">Facturation</option>
                        <option value="account" data-id="element-2689">Compte utilisateur</option>
                        <option value="feature" data-id="element-2690">
                          Suggestion de fonctionnalité
                        </option>
                        <option value="other" data-id="element-2691">Autre</option>
                      </select>
                    </div>
                    <div data-id="element-2692">
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2693">
                        Message
                      </label>
                      <textarea id="message" name="message" rows={4} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-blue-500 focus:border-blue-500" placeholder="Décrivez votre problème ou votre question..." data-id="element-2694"></textarea>
                    </div>
                    <div className="flex items-start" data-id="element-2695">
                      <div className="flex items-center h-5" data-id="element-2696">
                        <input id="terms" name="terms" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-2697" />
                      </div>
                      <div className="ml-3 text-sm" data-id="element-2698">
                        <label htmlFor="terms" className="text-gray-500" data-id="element-2699">
                          J'accepte que mes données soient traitées conformément
                          à la politique de confidentialité
                        </label>
                      </div>
                    </div>
                    <div data-id="element-2700">
                      <Button variant="primary" className="w-full shadow-md shadow-yellow-500/20" data-id="element-2701">
                        Envoyer le message
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>;
}